import React from 'react';
import { H1, H3, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Amount,
} from './styles';

const SALES_DASHBOARD_QUERY = gql`
  query SalesDashboard {
    dashboardMetrics {
      draftOrders
    }
    orders(limit: 30, offset: 0) {
      id
      orderNo
      orderStatus
      finalAmount
      orderAmount
      createdAt
      destinationCountry
    }
  }
`;

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const SalesDashboard: React.FC = () => {
  const { data } = useGraphQLQuery<{
    dashboardMetrics?: { draftOrders?: number };
    orders?: Array<{ id: number; orderNo?: string; orderStatus?: number; finalAmount?: number; orderAmount?: number; createdAt?: string; destinationCountry?: string }>;
  }>(SALES_DASHBOARD_QUERY, {});

  const draftOrders = data?.dashboardMetrics?.draftOrders ?? 0;
  const orders = data?.orders ?? [];

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>Sales Pipeline</H1>
          <Subtitle>Every order in flight, and the drafts that still need a follow-up.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Draft Orders</StatTitle>
              <StatValue>{draftOrders}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Pencil" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Orders (latest 30)</StatTitle>
              <StatValue>{orders.length}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Package" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <OrdersHeader>
          <H3 style={{ margin: 0 }}>Recent Orders</H3>
        </OrdersHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Order No</TableHeader>
                <TableHeader>Destination</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Amount</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                  <TableCell>{o.orderNo ?? o.id}</TableCell>
                  <TableCell>{o.destinationCountry ?? '—'}</TableCell>
                  <TableCell>{o.orderStatus === 0 ? 'Placed' : o.orderStatus === 1 ? 'Confirmed' : (o.orderStatus ?? '—')}</TableCell>
                  <TableCell>{o.createdAt ? o.createdAt.slice(0, 10) : '—'}</TableCell>
                  <TableCell><Amount>{formatAmount(o.finalAmount ?? o.orderAmount)}</Amount></TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No orders yet</span></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </OrdersCard>
    </DashboardContainer>
  );
};
